import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  ScanSearch,
  UploadCloud,
  FileText,
  X,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Stethoscope,
  ClipboardList,
  History,
  ChevronDown,
  ExternalLink,
  Loader2,
  ShieldAlert,
  Lightbulb,
} from "lucide-react";
import {
  analyzeDocument,
  getAnalysisHistory,
} from "../../services/oprations/documentAnalysisAPI";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

const SEVERITY_STYLES = {
  normal: {
    label: "Normal",
    badge: "bg-green-100 text-green-700 border-green-200",
    darkBadge: "bg-green-500/15 text-green-300 border-green-500/30",
    dot: "bg-green-500",
    icon: CheckCircle2,
  },
  attention: {
    label: "Needs Attention",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    darkBadge: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    dot: "bg-amber-500",
    icon: AlertTriangle,
  },
  urgent: {
    label: "Urgent",
    badge: "bg-red-100 text-red-700 border-red-200",
    darkBadge: "bg-red-500/15 text-red-300 border-red-500/30",
    dot: "bg-red-500",
    icon: ShieldAlert,
  },
};

const LIKELIHOOD_STYLES = {
  low: "bg-blue-100 text-blue-700",
  moderate: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
};

const SeverityBadge = ({ severity, darkMode }) => {
  const style = SEVERITY_STYLES[severity] || SEVERITY_STYLES.normal;
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${
        darkMode ? style.darkBadge : style.badge
      }`}
    >
      <Icon size={15} />
      {style.label}
    </span>
  );
};

const AnalysisResult = ({ analysis, darkMode }) => {
  if (!analysis) return null;

  const cardBase = darkMode
    ? "bg-[#00254D] border-[#003366]"
    : "bg-white border-gray-100";
  const headingText = darkMode ? "text-gray-100" : "text-gray-800";
  const bodyText = darkMode ? "text-gray-300" : "text-gray-600";

  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className={`rounded-2xl p-6 shadow-lg border ${cardBase}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h3 className={`text-lg font-bold ${headingText}`}>
                {analysis.document_type || "AI Analysis"}
              </h3>
              <p className={`text-xs ${bodyText}`}>AI-generated analysis</p>
            </div>
          </div>
          <SeverityBadge severity={analysis.severity} darkMode={darkMode} />
        </div>
        <p className={`leading-relaxed ${bodyText}`}>{analysis.summary}</p>
      </div>

      {/* Findings + Conditions grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {analysis.key_findings?.length > 0 && (
          <div className={`rounded-2xl p-6 shadow-lg border ${cardBase}`}>
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={18} className="text-indigo-500" />
              <h4 className={`font-bold ${headingText}`}>Key Findings</h4>
            </div>
            <ul className="space-y-3">
              {analysis.key_findings.map((finding, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                  <span className={`text-sm leading-relaxed ${bodyText}`}>
                    {finding}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {analysis.possible_conditions?.length > 0 && (
          <div className={`rounded-2xl p-6 shadow-lg border ${cardBase}`}>
            <div className="flex items-center gap-2 mb-4">
              <Stethoscope size={18} className="text-purple-500" />
              <h4 className={`font-bold ${headingText}`}>
                Possible Conditions
              </h4>
            </div>
            <ul className="space-y-3">
              {analysis.possible_conditions.map((condition, i) => (
                <li
                  key={i}
                  className={`flex items-center justify-between gap-3 p-3 rounded-xl ${
                    darkMode ? "bg-[#003366]/50" : "bg-gray-50"
                  }`}
                >
                  <span className={`text-sm font-medium ${headingText}`}>
                    {condition.name}
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      LIKELIHOOD_STYLES[condition.likelihood] ||
                      LIKELIHOOD_STYLES.low
                    }`}
                  >
                    {condition.likelihood}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <div className={`rounded-2xl p-6 shadow-lg border ${cardBase}`}>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={18} className="text-amber-500" />
            <h4 className={`font-bold ${headingText}`}>Recommendations</h4>
          </div>
          <ul className="space-y-3">
            {analysis.recommendations.map((rec, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2
                  size={17}
                  className="mt-0.5 text-green-500 flex-shrink-0"
                />
                <span className={`text-sm leading-relaxed ${bodyText}`}>
                  {rec}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      {analysis.disclaimer && (
        <div
          className={`rounded-xl px-4 py-3 text-xs leading-relaxed border ${
            darkMode
              ? "bg-amber-500/10 border-amber-500/20 text-amber-200"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          <span className="font-semibold">Disclaimer: </span>
          {analysis.disclaimer}
        </div>
      )}
    </div>
  );
};

const HistoryCard = ({ record, expanded, onToggle, darkMode }) => {
  const style =
    SEVERITY_STYLES[record.analysis?.severity] || SEVERITY_STYLES.normal;
  const isImage = record.file_type?.startsWith("image/");

  return (
    <div
      className={`rounded-2xl shadow-md border overflow-hidden transition-all ${
        darkMode
          ? "bg-[#00254D] border-[#003366]"
          : "bg-white border-gray-100 hover:shadow-lg"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left"
      >
        {isImage && record.file_url ? (
          <img
            src={record.file_url}
            alt={record.file_name}
            className="w-12 h-12 rounded-xl object-cover border border-gray-200 flex-shrink-0"
          />
        ) : (
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              darkMode ? "bg-[#003366]" : "bg-indigo-50"
            }`}
          >
            <FileText size={22} className="text-indigo-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className={`font-semibold truncate ${
              darkMode ? "text-gray-100" : "text-gray-800"
            }`}
          >
            {record.analysis?.document_type || record.file_name}
          </div>
          <div
            className={`text-xs truncate ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {record.file_name} · {formatDate(record.createdAt)}
          </div>
        </div>
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot}`} />
        <ChevronDown
          size={18}
          className={`flex-shrink-0 transition-transform ${
            expanded ? "rotate-180" : ""
          } ${darkMode ? "text-gray-400" : "text-gray-500"}`}
        />
      </button>

      {expanded && (
        <div
          className={`px-4 pb-5 pt-1 border-t ${
            darkMode ? "border-[#003366]" : "border-gray-100"
          }`}
        >
          {record.user_note && (
            <p
              className={`text-sm italic mb-4 mt-3 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              "{record.user_note}"
            </p>
          )}
          <div className="mt-3">
            <AnalysisResult analysis={record.analysis} darkMode={darkMode} />
          </div>
          {record.file_url && (
            <a
              href={record.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-indigo-500 hover:text-indigo-600"
            >
              <ExternalLink size={14} />
              View original file
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentAnalysis = () => {
  const { darkMode } = useOutletContext();
  const user = useSelector((state) => state.profile?.user);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [note, setNote] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const records = await getAnalysisHistory();
      if (mounted) {
        setHistory(records || []);
        setHistoryLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileSelect = useCallback(
    (selected) => {
      if (!selected) return;
      if (!ACCEPTED_TYPES.includes(selected.type)) {
        toast.error("Unsupported file. Use JPG, PNG, WEBP or PDF.");
        return;
      }
      if (selected.size > MAX_FILE_SIZE) {
        toast.error("File is too large. Maximum size is 15MB.");
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFile(selected);
      setResult(null);
      setPreviewUrl(
        selected.type.startsWith("image/")
          ? URL.createObjectURL(selected)
          : null
      );
    },
    [previewUrl]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    const record = await analyzeDocument({ file, note: note.trim() });
    setAnalyzing(false);
    if (record) {
      setResult(record);
      setHistory((prev) => [record, ...prev]);
      toast.success("Analysis complete");
    }
  };

  const startNewAnalysis = () => {
    clearFile();
    setNote("");
    setResult(null);
  };

  const headingText = darkMode ? "text-gray-100" : "text-gray-800";
  const bodyText = darkMode ? "text-gray-300" : "text-gray-600";

  return (
    <div className="space-y-8 p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg">
            <ScanSearch size={24} className="text-white" />
          </div>
          <h1 className={`text-3xl font-bold ${headingText}`}>
            AI Document Analysis
          </h1>
        </div>
        <p className={`mt-2 ${bodyText}`}>
          Upload an X-ray, scan, photo or medical report (PDF) and our AI will
          analyze it for you
          {user?.role === "Doctor" ? " with clinical detail." : " in simple terms."}
        </p>
      </div>

      {/* Upload card */}
      <div
        className={`rounded-2xl p-6 shadow-lg border ${
          darkMode ? "bg-[#00254D] border-[#003366]" : "bg-white border-gray-100"
        }`}
      >
        {!file ? (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 sm:p-14 flex flex-col items-center justify-center text-center transition-all duration-300 ${
              dragActive
                ? "border-indigo-500 bg-indigo-50/60 scale-[1.01]"
                : darkMode
                ? "border-[#004080] hover:border-[#2aabdc] hover:bg-[#003366]/40"
                : "border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/40"
            }`}
          >
            <div
              className={`p-4 rounded-2xl mb-4 ${
                darkMode ? "bg-[#003366]" : "bg-indigo-50"
              }`}
            >
              <UploadCloud
                size={38}
                className={dragActive ? "text-indigo-500" : "text-[#2aabdc]"}
              />
            </div>
            <p className={`font-semibold text-lg ${headingText}`}>
              Drag & drop your file here
            </p>
            <p className={`text-sm mt-1 ${bodyText}`}>
              or <span className="text-indigo-500 font-medium">browse</span>{" "}
              from your device
            </p>
            <p
              className={`text-xs mt-4 ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            >
              X-rays · Scans · Photos · Reports — JPG, PNG, WEBP, PDF (max 15MB)
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-5">
            {/* File preview */}
            <div className="relative flex-shrink-0">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={file.name}
                  className="w-full sm:w-44 h-44 object-cover rounded-2xl border border-gray-200 shadow-md"
                />
              ) : (
                <div
                  className={`w-full sm:w-44 h-44 rounded-2xl flex flex-col items-center justify-center gap-2 border ${
                    darkMode
                      ? "bg-[#003366] border-[#004080]"
                      : "bg-indigo-50 border-indigo-100"
                  }`}
                >
                  <FileText size={44} className="text-indigo-500" />
                  <span className="text-xs font-semibold text-indigo-500 uppercase">
                    PDF
                  </span>
                </div>
              )}
              <button
                onClick={clearFile}
                disabled={analyzing}
                className="absolute -top-2 -right-2 p-1.5 rounded-full bg-red-500 text-white shadow-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                title="Remove file"
              >
                <X size={14} />
              </button>
            </div>

            {/* File info + note + action */}
            <div className="flex-1 flex flex-col gap-3">
              <div>
                <p className={`font-semibold truncate ${headingText}`}>
                  {file.name}
                </p>
                <p className={`text-xs ${bodyText}`}>
                  {formatBytes(file.size)}
                </p>
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={analyzing}
                rows={3}
                placeholder="Optional — describe your concern or ask a question about this file..."
                className={`w-full rounded-xl border-2 p-3 text-sm resize-none outline-none transition-colors ${
                  darkMode
                    ? "bg-[#003366]/50 border-[#004080] text-gray-100 placeholder-gray-400 focus:border-[#2aabdc]"
                    : "bg-white border-indigo-200 text-gray-800 focus:border-indigo-500"
                }`}
              />
              <button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="self-start inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {analyzing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Analyze with AI
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Analyzing state */}
      {analyzing && (
        <div
          className={`rounded-2xl p-10 shadow-lg border flex flex-col items-center justify-center text-center ${
            darkMode
              ? "bg-[#00254D] border-[#003366]"
              : "bg-white border-gray-100"
          }`}
        >
          <div className="relative mb-5">
            <div className="p-5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 animate-pulse">
              <ScanSearch size={34} className="text-white" />
            </div>
            <span className="absolute inset-0 rounded-full bg-indigo-400/40 animate-ping" />
          </div>
          <p className={`font-semibold text-lg ${headingText}`}>
            AI is examining your document...
          </p>
          <p className={`text-sm mt-1 ${bodyText}`}>
            This usually takes a few seconds
          </p>
        </div>
      )}

      {/* Result */}
      {result && !analyzing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-bold ${headingText}`}>
              Analysis Result
            </h2>
            <button
              onClick={startNewAnalysis}
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                darkMode
                  ? "text-[#2aabdc] hover:bg-[#003366]"
                  : "text-indigo-500 hover:bg-indigo-50"
              }`}
            >
              + New Analysis
            </button>
          </div>
          <AnalysisResult analysis={result.analysis} darkMode={darkMode} />
        </div>
      )}

      {/* History */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <History
            size={20}
            className={darkMode ? "text-gray-300" : "text-gray-600"}
          />
          <h2 className={`text-xl font-bold ${headingText}`}>
            Previous Analyses
          </h2>
        </div>
        {historyLoading ? (
          <div className={`flex items-center gap-2 text-sm ${bodyText}`}>
            <Loader2 size={16} className="animate-spin" />
            Loading history...
          </div>
        ) : history.length === 0 ? (
          <p className={`text-sm ${bodyText}`}>
            No analyses yet — upload your first document above.
          </p>
        ) : (
          <div className="space-y-3">
            {history.map((record) => (
              <HistoryCard
                key={record._id}
                record={record}
                expanded={expandedId === record._id}
                onToggle={() =>
                  setExpandedId(expandedId === record._id ? null : record._id)
                }
                darkMode={darkMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentAnalysis;
